<template>
  <div class="bpm-addr">
    <zm-form-item
      :label="__$t('billing.address.street_address')"
      :for="affix+'-address1'"
      aria-label="required"
      prop="address1"
      class="bpm-addr__required text-capitalize-form-item"
    >
      <div class="bpm-addr__content-address1" aria-label="required">
        <zm-autocomplete
          v-model="form.address1"
          :fetch-suggestions="querySearch"
          :id="affix+'-address1'"
          value-key="positionDesc"
          ref="autocomplete"
          @select="handleSelectAddr"
          :popper-class="ifListShow? 'bpm-addr__suggest-list':''"
        ></zm-autocomplete>
        <zm-tooltip v-if="!isBillToAddress" v-model="address1Tooltip" class="item" :content="__$t('billing.addr.street_info')" placement="bottom"
                    :aria-label="'learn more about '+__$t('billing.address.street_address')" role="button">
          <i class="bpm-addr-tip zm-icon-info-outline" />
        </zm-tooltip>
      </div>
    </zm-form-item>
    <div
      v-if="!additionalAddresses"
      class="bpm-addr__add-adress text-capitalize-form-item"
      @click="showAdditionalAddresses"
      tabindex="0"
      :aria-label="'add '+ __$t('billing.addr.line_2')"
      role="button"
      :label="'add '+__$t('billing.addr.line_2')"
    >+ {{__$t('billing.addr.line_2')}}</div>
    <zm-form-item prop="address2" class="text-capitalize-form-item" :label="__$t('billing.address.address_line2')" :for="affix+'-address2'" v-if="additionalAddresses">
      <zm-input :id="affix+'-address2'" v-model="form.address2" :ref="affix+'address2'"/>
    </zm-form-item>
    <zm-form-item class="bpm-addr__country-form">
      <zm-form-item :label="__$t('billing.zip_postal')" :for="affix+'-zip'" prop="zip" class="bpm-addr__required text-capitalize-form-item"
                    aria-label="required"
      >
        <zm-input :id="affix+'-zip'" v-model="form.zip" maxlength="20"></zm-input>
      </zm-form-item>
      <zm-form-item :label="__$t('billing.city')" :for="affix+'-city'" prop="city" class="bpm-addr__required text-capitalize-form-item"
                    aria-label="required">
        <zm-input :id="affix+'-city'" v-model="form.city" :maxlength="40"></zm-input>
      </zm-form-item>

      <zm-form-item :label="__$t('billing.state')" :for="affix+'-state'" class="bpm-addr__required text-capitalize-form-item" prop="state"
                    :aria-label="isStateRequired ? 'required':''"
      >
        <zm-select
          v-if="stateMap.length"
          v-model="form.state"
          filterable
          :popper-append-to-body="true"
          class="state-select"
          :aria-label="__$t('billing.state')"
        >
          <zm-option
            :id="affix+'-state'"
            v-for="(state, index) in stateMap"
            :key="index"
            :label="__$t(state.label)"
            :value="state.value"
          ></zm-option>
        </zm-select>
        <zm-input v-else :id="affix+'-state'" v-model="form.state" :maxlength="40"></zm-input>
      </zm-form-item>
    </zm-form-item>
    <zm-form-item
      :label="__$t('billing.country_region')"
      size="xLarge"
      class="bpm-addr__required text-capitalize-form-item"
      :class="disableCountrySelect ? 'disabled-country-select':''"
      :for="affix+'-country'"
      prop="country"
      :aria-label="__$t('billing.country_region')+',required'"
    >
      <zm-tooltip :disabled="!disableCountrySelect" :content="countrySelectionTooltip">
        <zm-select
          filterable v-model="form.country"
          :id="affix+'-country'"
          :popper-append-to-body="true" ref="countrySelect"
          :label="__$t('billing.country_region')" :disabled="disableCountrySelect">
          <zm-option
            v-for="(country, index) in countryList"
            :key="index"
            :label="country.label"
            :value="country.value"
          ></zm-option>
        </zm-select>
      </zm-tooltip>
      <template v-if='isShowInrNonIndiaCountryError' v-slot:error>
        <transition name="zm-zoom-in-top">
          <div
            class="zm-form-item__error zm-form-item__error--inline"
            role="alert"
            aria-label=""
          >
            <span aria-hidden="true">
              {{ __$t('billing.error.inr_non_india_error') }} <zm-button type="link" @click="switchToUsd">{{ __$t('billing.error.inr_non_india_error_switch_to_usd') }}</zm-button>.
            </span>
          </div>
        </transition>
      </template>
    </zm-form-item>
    <use-case-form v-if="isVideoSDK" :form="form"/>
    <zm-form-item v-if="showPhone" aria-label="required" prop="phone" class="bpm-addr__required text-capitalize-form-item" :label="__$t('billing.address.phone')" :for="affix+'-phone'">
      <zm-input :id="affix+'-phone'" v-model="form.phone" maxlength="40"/>
    </zm-form-item>
    <template v-if='withOutCcAccount && !isBillToAddress'>
      <div
        v-if="!showCompanyOrgName"
        class="bpm-addr__add-adress text-capitalize-form-item"
        @click="displayCompanyOrgName"
        tabindex="0"
        :aria-label="'add '+ __$t('billing.company_organization')"
        role="button"
        :label="'add '+__$t('billing.company_organization')"
      >+ {{__$t('billing.company_organization')}}</div>
      <zm-form-item v-if="showCompanyOrgName" aria-label="required" prop="companyOrgName" class="text-capitalize-form-item" :label="__$t('billing.company_organization')" :for="affix+'-companyOrgName'">
        <zm-input :id="affix+'-companyOrgName'" v-model="form.companyName" maxlength="40" :ref="affix+'companyOrgName'"/>
      </zm-form-item>
    </template>
  </div>
</template>

<script>
import formMixins from './mixins/form-mixin.js';
import { statesOfCA, statesOfIN, statesOfUS ,US_short } from '../../common/regionUtils';
import { autocomplete, getAddrDetail } from '../../apis/address';
import { assign } from 'lodash';
import {getLang, isEmptyVal} from '../../utils'
import {momentI18nMap, translateMixins} from '../../plugins/i18n'
import Enums from "../../common/Enums";
import { isInrCurrency, setCurrency } from '../../utils/currency';
import { COUNTRY_CODE_INDIA, CURRENCY_USD } from '../../common/constant';
import useCaseForm from "../contact-info/use-case-form.vue";
import {APP_NAMESPACE} from "../../store";
import TrackingMixin from "../mixins/trackingMixin";
import {mapGetters} from "vuex";

export default {
  name: 'addressBaseForm',
  mixins: [formMixins, translateMixins, TrackingMixin],
  components: {useCaseForm},
  data() {
    return {
      address1Tooltip: false,
      additionalAddresses: false,
      showCompanyOrgName: false,
      stateMap: [],
      showAddrList: false,
      link:'/',
      ifListShow: false,
      isStateRequired: false,
    };
  },
  props: {
    tracking: Function,
    form: {
      type: Object,
      default () {
        return {}
      }
    },
    disableCountrySelect: {
      type: Boolean,
      default: false,
    },
    affix: {
      type: String,
      default: 'addr',
    },
    showPhone: {
      type: Boolean,
      default: false,
    },
    // in opc, this attr is get from selected plans
    isVideoSDK: {
      type: Boolean,
      default: false,
    },
    countrySelectionTooltip: {
      type: String,
      default: '',
    }
  },
  computed: {
    ...mapGetters(APP_NAMESPACE, ['countryList','addressInfo', 'withOutCcAccount']),
    isBillToAddress() {
      return this.affix === "bill-to";
    },
    isShowInrNonIndiaCountryError() {
      return !this.isBillToAddress && isInrCurrency() && this.form.country && this.form.country !== COUNTRY_CODE_INDIA;
    },
  },
  watch: {
    address1Tooltip: {
      immediate: true,
      handler(v) {
        v && this.tracking && this.tracking('street address - tooltip')
      }
    },
    'form.country'(val,oldV) {
      this.handleCountryChange(val,oldV);
    },
    'form.state'(val,oldV) {
      this.handleStateChange(val,oldV);
    },
    'form.companyName': {
      immediate: true,
      handler(value) {
        if (value && value.trim() !== '') {
          this.showCompanyOrgName = true
        }
      }
    },
    'form.address2': {
      immediate: true,
      handler(value) {
        if (value && value.trim() !== '') {
          this.additionalAddresses = true
        }
      }
    }
  },
  mounted(){
    if (!this.form.country && isInrCurrency()) {
      this.form.country = COUNTRY_CODE_INDIA;
    }
    if(this.form.state){
      this.handleStateChange(this.form.state);
    }
  },
  methods: {
    handleCountryChange(val) {
      if (this.isShowInrNonIndiaCountryError) {
        this.emitTracking('showInrNonIndiaCountryError')
      }
      this.$emit('closeBlock')
      if(!(this.countryList.find(item => item.value === val))){
        this.form.country = ''
      }
      const countriesWithMap = {
        US: statesOfUS,
        CA: statesOfCA,
        IN: statesOfIN,
      };
      if (countriesWithMap[val]) {
        if(!(countriesWithMap[val].find(item => item.value === this.form.state))){
          this.isStateRequired = true
          this.form.state = ''
        }
        this.stateMap = [{ label: 'billing.select_state', value: '' }, ...countriesWithMap[val]];
      } else {
        this.stateMap = [];
        this.isStateRequired = false
      }
      // PRO INCREASE - SoldTo country change may bring Price change of Pro Monthly and Yearly
      if (!this.isBillToAddress) {
        this.$emit("checkProMonthlyAndYearlyPriceChange", val)
      }
    },
    handleStateChange(val) {
      const country = this.form.country
      const countriesWithMap = {
        US: statesOfUS,
        CA: statesOfCA,
        IN: statesOfIN,
      };
      if (countriesWithMap[country]) {
        if(!(countriesWithMap[country].find(item => item.value === val))){
          this.form.state = ''
          this.isStateRequired = false
        }
        this.stateMap = [{ label: 'billing.select_state', value: '' }, ...countriesWithMap[country]];
        this.isStateRequired = true
      }
    },
    showAdditionalAddresses() {
      this.additionalAddresses = true;
      this.$nextTick(()=>{
        this.$refs[this.affix+'address2'].focus()
      })
    },
    displayCompanyOrgName() {
      this.showCompanyOrgName = true;
      this.$nextTick(()=>{
        this.$refs[this.affix+'companyOrgName'].focus()
      })
    },
    async querySearch(qs, cb) {
      this.$refs.countrySelect.blur()
      const lang = momentI18nMap[getLang] || 'en'
      const sameAddr = qs === this.addressInfo?.address1
      if (qs.trim().length > 2 && !sameAddr) {
        const { result } = await autocomplete({
          input: qs,
          language: lang,
          ...(!this.isBillToAddress && isInrCurrency() ? { country: COUNTRY_CODE_INDIA } : {}),
        });
        this.$refs.autocomplete.$data.activated = true;
        this.ifListShow = true
        cb(result);
      } else {
        this.ifListShow = false
        cb([]);
      }
    },
    async handleSelectAddr(v) {
      const { placeId, positionDesc } = v;
      const lang = momentI18nMap[getLang] || 'en'
      const { result } = await getAddrDetail({
        placeId,
        language: lang
      });
      const addrDetail = JSON.parse(result?.positionDesc || {});
      const { city, country, postalCode: zip, state } = addrDetail;
      assign(this.form, { city, country, zip, state, address1: this.createAddress(positionDesc, addrDetail) });
    },
    createAddress(fullAddr, addrDetail) {
      const {city, postalCode, state } = addrDetail;
      const country = addrDetail.adminCountry || addrDetail.country || ''
      const stateCode = new Enums(US_short)[state]
      const altIsoToShortCountryCodes = {'US': 'USA', 'GB': 'UK'};
      const countryLabel = (this.countryList.find(item => item.value === country) || {}).label;
      const altCountryCode = altIsoToShortCountryCodes[country];
      const parsedAddr = fullAddr.split(', ')
        .map(item => item.trim())
        .filter(item => {
          return ![city, country, countryLabel, altCountryCode, postalCode, state , stateCode].includes(item);
        })
        .join(', ');

      return isEmptyVal(parsedAddr) ? fullAddr : parsedAddr;
    },
    focus(){
      this.$refs?.autocomplete?.focus?.()
    },
    switchToUsd() {
      this.emitTracking('switchToUsd')
      setCurrency(CURRENCY_USD);
    },
  },
};
</script>
<style lang="scss">
@import '../../styles/common.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';
.bpm-addr {
  .disabled-country-select {
    .zm-select {
      background: rgba(247, 247, 248, 1);
    }
  }
  &__required {
    .zm-input__label,
    .zm-select__label {
      &::after {
        display: inline-block;
        content: '*';
        margin-left: 4px;
      }
    }
  }

  .zm-button--plain:not(.is-ghost) {
    border:none;
  }
  &-tip {
    display: inline-block;
    cursor: pointer;
    margin-left: 10px;
    color: $color-main;
  }
  &__content {
    &-address1 {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
  &__add-adress {
    margin-top: 8px;
    color: $color-main;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    padding: 0;
    &:hover,
    &:active {
      background: transparent;
      color: $color-main;
    }
  }
  &__country-form {
    .zm-form-item__content {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      width: 100%;
      &::before {
        display: none;
      }
      .zm-form-item {
        width: 32%;
        margin-bottom: 0;
        .zm-select-input__inner {
          text-overflow: ellipsis;
        }
      }
    }
  }
  @include media(mobile) {
    &__country-form {
      .zm-form-item__content {
        flex-direction: column;
        &::before {
          display: none;
        }
        .zm-form-item {
          width: 100%;
          margin-bottom: 12px;
          &:last-of-type {
            margin-bottom: 0;
          }
        }
      }
    }
  }
  &__suggest-list {
    &::after {
      display: block;
      width: 98%;
      line-height: 50px;
      text-align: right;
      height: 50px;
      background:url(~../../assets/google-icon.svg);
      background-repeat: no-repeat;
      background-position: right;
      background-size: 80px;
      content: 'powered by';
      font-size: 12px;
      padding-right: 80px;
    }
  }
  .state-select{
    .zm-select-input__inner{
      width: 150px;
    }
  }
}
</style>
